import React, { useState, useEffect } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faUserTie, faKey, faCheck, faTimes,
  faShieldHalved, faUser, faEnvelope, faPhone, faLock,
  faToggleOn, faToggleOff, faEye,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatusDef } from '../../components/ui';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AdminEmployee {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  role: string;
  permissions: any;
  is_active: boolean;
  twofa_enabled: boolean;
  last_login_at?: string;
  created_at: string;
}

// ─── Status maps (module-level) ───────────────────────────────────────────────

const ROLE_STATUS_MAP: Record<string, StatusDef> = {
  super_admin: { bg: 'bg-red-100',    text: 'text-red-700',    icon: faShieldHalved, label: 'Super Admin' },
  manager:     { bg: 'bg-amber-100',  text: 'text-amber-700',  icon: faUserTie,      label: 'Manager'     },
  operator:    { bg: 'bg-blue-100',   text: 'text-blue-700',   icon: faUser,         label: 'Operator'    },
  viewer:      { bg: 'bg-gray-100',   text: 'text-gray-600',   icon: faEye,          label: 'Viewer'      },
};

const ACTIVE_STATUS_MAP: Record<string, StatusDef> = {
  active:   { bg: 'bg-green-100', text: 'text-green-700', icon: faCheck,  label: 'ใช้งาน' },
  inactive: { bg: 'bg-red-100',   text: 'text-red-600',   icon: faTimes,  label: 'ระงับ'  },
};

// ─── Constants ────────────────────────────────────────────────────────────────

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

const PERMISSION_LABELS: Record<string, string> = {
  view_dashboard:         'ดู Dashboard',
  view_merchants:         'ดู Merchants',
  create_merchants:       'สร้าง Merchants',
  edit_merchants:         'แก้ไข Merchants',
  delete_merchants:       'ลบ Merchants',
  view_banks:             'ดูธนาคาร',
  manage_banks:           'จัดการธนาคาร',
  view_orders:            'ดู Orders',
  manage_orders:          'จัดการ Orders',
  cancel_order:           'ยกเลิก Order',
  view_transactions:      'ดู Transactions',
  view_settlements:       'ดู Settlements',
  approve_settlements:    'อนุมัติ Settlements',
  view_payouts:           'ดู Payouts',
  approve_payouts:        'อนุมัติ Payouts',
  view_webhooks:          'ดู Webhooks',
  view_reports:           'ดูรายงาน',
  view_employees:         'ดูพนักงาน',
  manage_employees:       'จัดการพนักงาน',
  system_settings:        'ตั้งค่าระบบ',
  approve_slip_upload:    'อนุมัติฝากโดยแนบสลิป',
  approve_slip_no_verify: 'อนุมัติสลิปโดยไม่เช็คความถูกต้อง',
};

const DEFAULT_PERMISSIONS = (): Record<string, boolean> =>
  Object.fromEntries(Object.keys(PERMISSION_LABELS).map(k => [k, false])) as any;

function getDefaultPermissions(role: string): Record<string, boolean> {
  const p: Record<string, boolean> = { ...DEFAULT_PERMISSIONS(), view_dashboard: true };
  switch (role) {
    case 'super_admin': Object.keys(p).forEach(k => { p[k] = true; }); break;
    case 'manager':
      Object.assign(p, {
        view_merchants: true, edit_merchants: true, view_banks: true,
        view_orders: true, manage_orders: true, view_transactions: true,
        view_settlements: true, approve_settlements: true, view_payouts: true,
        approve_payouts: true, view_webhooks: true, view_reports: true, view_employees: true,
      });
      break;
    case 'operator':
      Object.assign(p, {
        view_merchants: true, view_banks: true, view_orders: true, manage_orders: true,
        view_transactions: true, view_settlements: true, view_payouts: true, view_reports: true,
      });
      break;
    case 'viewer':
      Object.assign(p, {
        view_merchants: true, view_banks: true, view_orders: true, view_transactions: true,
        view_settlements: true, view_payouts: true, view_reports: true,
      });
      break;
  }
  return p;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const AdminEmployeesPage: React.FC = () => {
  const [employees, setEmployees] = useState<AdminEmployee[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<AdminEmployee | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    email: '', password: '', full_name: '', phone: '',
    role: 'viewer' as string, is_active: true,
  });
  const [permissions, setPermissions] = useState<Record<string, boolean>>(getDefaultPermissions('viewer'));

  useEffect(() => { loadEmployees(); }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/admin/employees');
      setEmployees(res.data.data.employees || []);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลพนักงานได้');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (employee?: AdminEmployee) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        email: employee.email, password: '', full_name: employee.full_name,
        phone: employee.phone || '', role: employee.role, is_active: employee.is_active,
      });
      setPermissions({ ...DEFAULT_PERMISSIONS(), ...(employee.permissions || {}) });
    } else {
      setEditingEmployee(null);
      setFormData({ email: '', password: '', full_name: '', phone: '', role: 'viewer', is_active: true });
      setPermissions(getDefaultPermissions('viewer'));
    }
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setEditingEmployee(null); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEmployee && !formData.password) { toast.error('กรุณากรอก Password'); return; }
    setSubmitting(true);
    try {
      const payload: any = { ...formData, permissions };
      if (editingEmployee && !payload.password) delete payload.password;

      if (editingEmployee) {
        await merchantApi.put(`/admin/employees/${editingEmployee.id}`, payload);
        toast.success('อัพเดทพนักงานสำเร็จ');
      } else {
        await merchantApi.post('/admin/employees', payload);
        toast.success('สร้างพนักงานสำเร็จ');
      }
      handleCloseModal();
      loadEmployees();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('ยืนยันการลบพนักงานนี้?')) return;
    try {
      await merchantApi.delete(`/admin/employees/${id}`);
      toast.success('ลบพนักงานสำเร็จ');
      loadEmployees();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'ไม่สามารถลบพนักงานได้');
    }
  };

  const handleRoleChange = (newRole: string) => {
    setFormData(f => ({ ...f, role: newRole }));
    setPermissions(getDefaultPermissions(newRole));
  };

  // ─── Table columns ───────────────────────────────────────────────────────────

  const columns: Column<AdminEmployee>[] = [
    {
      key: 'full_name',
      header: 'ชื่อ-นามสกุล',
      render: (emp) => (
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
            style={{ background: 'var(--theme-primary-gradient)' }}
          >
            {emp.full_name[0]?.toUpperCase()}
          </div>
          <span className="font-medium text-gray-800 dark:text-slate-100">{emp.full_name}</span>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (emp) => <span className="text-sm text-gray-600 dark:text-slate-300">{emp.email}</span>,
    },
    {
      key: 'phone',
      header: 'เบอร์โทร',
      render: (emp) => <span className="text-sm text-gray-600 dark:text-slate-400">{emp.phone || '-'}</span>,
    },
    {
      key: 'role',
      header: 'บทบาท',
      render: (emp) => <StatusBadge status={emp.role} statusMap={ROLE_STATUS_MAP} />,
    },
    {
      key: 'twofa_enabled',
      header: '2FA',
      render: (emp) => emp.twofa_enabled ? (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-medium">
          <FontAwesomeIcon icon={faKey} /> เปิด
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 rounded text-xs font-medium">
          ปิด
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'สถานะ',
      render: (emp) => <StatusBadge status={emp.is_active ? 'active' : 'inactive'} statusMap={ACTIVE_STATUS_MAP} />,
    },
    {
      key: 'last_login_at',
      header: 'Login ล่าสุด',
      render: (emp) => (
        <span className="text-sm text-gray-500 dark:text-slate-400">
          {emp.last_login_at ? new Date(emp.last_login_at).toLocaleString('th-TH') : '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (emp) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleOpenModal(emp)}
            className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center transition-colors"
            title="แก้ไข"
          >
            <FontAwesomeIcon icon={faEdit} className="text-xs" />
          </button>
          <button
            onClick={() => handleDelete(emp.id)}
            className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 flex items-center justify-center transition-colors"
            title="ลบ"
          >
            <FontAwesomeIcon icon={faTrash} className="text-xs" />
          </button>
        </div>
      ),
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-full overflow-x-hidden">

      <UiPageHeader
        icon={faUserTie}
        title="Admin Employees"
        subtitle={`จัดการพนักงานแอดมิน • ${employees.length} คน`}
        actions={
          <UiButton icon={faPlus} onClick={() => handleOpenModal()}>
            เพิ่มพนักงาน
          </UiButton>
        }
      />

      <DataTable
        data={employees}
        columns={columns}
        loading={loading}
        keyExtractor={(emp) => emp.id}
        hoverable
        compact
        emptyMessage="ไม่มีข้อมูลพนักงาน"
        emptyIcon={<FontAwesomeIcon icon={faUserTie} className="text-5xl text-gray-200 dark:text-slate-600" />}
      />

      {/* Create / Edit Modal */}
      <UiModal
        open={showModal}
        onClose={handleCloseModal}
        title={editingEmployee ? 'แก้ไขพนักงาน' : 'เพิ่มพนักงานใหม่'}
        icon={editingEmployee ? faEdit : faPlus}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="lg"
        scrollable
      >
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Row 1: Email + Password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faEnvelope} className="mr-1" /> Email *
              </label>
              <input
                type="email" required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faLock} className="mr-1" /> Password {!editingEmployee && '*'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingEmployee ? 'เว้นว่างถ้าไม่เปลี่ยน' : ''}
                required={!editingEmployee}
                className={INPUT_CLASS}
              />
            </div>
          </div>

          {/* Row 2: Name + Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faUser} className="mr-1" /> ชื่อ-นามสกุล *
              </label>
              <input
                type="text" required
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faPhone} className="mr-1" /> เบอร์โทร
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={INPUT_CLASS}
              />
            </div>
          </div>

          {/* Row 3: Role + Active toggle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>
                <FontAwesomeIcon icon={faShieldHalved} className="mr-1" /> บทบาท *
              </label>
              <select
                required
                value={formData.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="viewer">Viewer (ดูอย่างเดียว)</option>
                <option value="operator">Operator (ดู + จัดการบางส่วน)</option>
                <option value="manager">Manager (จัดการส่วนใหญ่)</option>
                <option value="super_admin">Super Admin (เต็มสิทธิ์)</option>
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>สถานะ</label>
              <div className="flex items-center gap-2 h-[42px]">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: true })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${formData.is_active
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'}`}
                >
                  <FontAwesomeIcon icon={faToggleOn} /> ใช้งาน
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: false })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${!formData.is_active
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'}`}
                >
                  <FontAwesomeIcon icon={faToggleOff} /> ระงับ
                </button>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 dark:text-slate-100 mb-1 flex items-center gap-2">
              <FontAwesomeIcon icon={faKey} style={{ color: 'var(--theme-primary)' }} />
              สิทธิ์การเข้าถึง
            </h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-4">
              สิทธิ์จะปรับตามบทบาทอัตโนมัติ คุณสามารถแก้ไขเพิ่มเติมได้
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.keys(permissions).map((key) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={permissions[key] ?? false}
                    onChange={(e) => setPermissions({ ...permissions, [key]: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-600 dark:text-slate-300 group-hover:text-gray-800 dark:group-hover:text-slate-100 transition-colors">
                    {PERMISSION_LABELS[key] ?? key}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-1">
            <UiButton variant="secondary" onClick={handleCloseModal} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton type="submit" loading={submitting} className="flex-1 justify-center">
              {editingEmployee ? 'บันทึก' : 'เพิ่มพนักงาน'}
            </UiButton>
          </div>

        </form>
      </UiModal>

    </div>
  );
};

export default AdminEmployeesPage;
